import React, { useReducer, useEffect } from 'react' // richiesto da CRA 2: no JSX transform in react-scripts 2.x — eslint-disable-line no-unused-vars
import { Button, Dimmer, Form, Icon, Loader, Message, Table } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

// Etichette dei mesi (1-based: MONTH_LABELS[month-1]). Allineato a StepBulkApprove/StepSetSuperCutoff.
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Formatta un valore numerico come valuta EUR in locale italiano.
 * Restituisce '—' se il valore è null o undefined.
 * @param {number|null|undefined} value
 * @returns {string}
 */
const formatCurrency = (value) =>
  value != null
    ? '\u20AC ' + value.toLocaleString('it-IT', { minimumFractionDigits: 2 })
    : '\u2014'

// definiti fuori dal componente per evitare la ricreazione della referenza a ogni render
const initialState = {
  confirmVisible: false,
  submitting: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SHOW_CONFIRM':
      return { ...state, confirmVisible: true }
    case 'HIDE_CONFIRM':
      return { ...state, confirmVisible: false }
    case 'SUBMIT_START':
      return { ...state, submitting: true, confirmVisible: false }
    case 'SUBMIT_DONE':
      return { ...state, submitting: false }
    default:
      return state
  }
}

/**
 * Step 3 — Preview e azzeramento ricavi su budget pending.
 * Mostra la lista dei budget impattati prima di eseguire l'azzeramento.
 *
 * Il periodo non e scelto liberamente dall'utente: viene derivato dalla lista cutoff PM
 * caricata dal container e corrisponde al mese successivo all'ultimo super cutoff registrato.
 * Il payload inviato a onPreview/onExecute resta { cutoff_month, cutoff_year } (contratto BE invariato),
 * con valori presi direttamente da nextPeriod.
 *
 * Props:
 *   preview            {object}    [opzionale] { budgets_to_reset, budget_details: [] }
 *   result             {object}    [opzionale] { budgets_reset }
 *   nextPeriod         {object}    [opzionale] { month, year, date_start, date_end } derivato da cutoff_pm.
 *                                  Se null, lo step e bloccato: il messaggio di errore informa l'utente
 *                                  che non e possibile determinare il periodo target.
 *   loadingNextPeriod  {boolean}   [opzionale] True mentre la lista cutoff PM e in caricamento. Default: false
 *   onPreview          {Function}  Firma: fn({ cutoff_month, cutoff_year })
 *   onExecute          {Function}  Firma: fn({ cutoff_month, cutoff_year }). Il parent deve transitare
 *                                  loadingExecute per true durante l'esecuzione, altrimenti il bottone
 *                                  rimane disabilitato indefinitamente dopo il submit.
 *   loadingPreview     {boolean}   [opzionale] Default: false
 *   loadingExecute     {boolean}   [opzionale] Default: false
 */
const StepRevenueReset = ({
  preview,
  result,
  nextPeriod,
  loadingNextPeriod = false,
  onPreview,
  onExecute,
  loadingPreview = false,
  loadingExecute = false,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { confirmVisible, submitting } = state

  const isExecuting = submitting || loadingExecute

  // Periodo derivato dal nextPeriod: nessun calcolo locale.
  const hasNextPeriod = nextPeriod !== null && nextPeriod !== undefined
  const month = hasNextPeriod ? nextPeriod.month : null
  const year = hasNextPeriod ? nextPeriod.year : null
  const monthLabel = (month !== null) ? MONTH_LABELS[month - 1] : ''

  // Le opzioni delle select contengono SOLO il valore vincolato: l'utente vede esplicitamente
  // il mese e l'anno target, ma non puo' cambiarli. Coerente con StepBulkApprove/StepSetSuperCutoff.
  const monthOptions = hasNextPeriod
    ? [{ key: month, value: month, text: monthLabel }]
    : []
  const yearOptions = hasNextPeriod
    ? [{ key: year, value: year, text: String(year) }]
    : []

  // Gating: i bottoni si abilitano solo se il periodo e disponibile e non in loading.
  const periodReady = hasNextPeriod && !loadingNextPeriod
  const periodBlocked = !loadingNextPeriod && !hasNextPeriod

  const handlePreview = () => {
    if (!periodReady) return
    onPreview({ cutoff_month: month, cutoff_year: year })
  }

  const handleExecute = () => {
    if (!periodReady) return
    dispatch({ type: 'SUBMIT_START' })
    try {
      onExecute({ cutoff_month: month, cutoff_year: year })
    } catch (_) {
      // onExecute ha lanciato un'eccezione sincrona: reset immediato per sbloccare l'UI
      dispatch({ type: 'SUBMIT_DONE' })
    }
  }

  // Sincronizza submitting con il completamento segnalato dal parent tramite loadingExecute.
  // Presuppone che il parent transiti loadingExecute per true durante l'esecuzione:
  // se non lo fa, submitting resta true e il bottone rimane disabilitato indefinitamente.
  useEffect(() => {
    if (!loadingExecute && submitting) {
      dispatch({ type: 'SUBMIT_DONE' })
    }
  }, [loadingExecute, submitting])

  return (
    <div role="region" aria-labelledby="revenue-reset-step-title">
      {/* Titolo nascosto visivamente — ancora per il landmark dello step */}
      <h3 id="revenue-reset-step-title" className="sr-only">Revenue Reset</h3>
      <p className="supercutoff-step-description">
        Resets revenues and total revenues to zero on pending budgets (not in CR status) for the target month.
        The period is locked to the month following the latest super cutoff: review the list of affected
        budgets before confirming.
      </p>

      {/* Loader sulla selezione finche cutoff_pm non e arrivato */}
      {loadingNextPeriod && (
        <div className="supercutoff-period-loader">
          <Dimmer active inverted>
            <Loader inverted content="Loading target period" size="small" />
          </Dimmer>
        </div>
      )}

      {/* Errore bloccante: cutoff_pm caricato ma vuoto/non valido. Non mostrato in fase completed. */}
      {periodBlocked && !result && (
        <Message negative icon>
          <Icon name="ban" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Cannot determine target period</Message.Header>
            <p>
              The super cutoff list is unavailable or empty. Reload the page to retry, or
              contact the administrator if the problem persists.
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Form selezione periodo — visibile solo se il periodo e disponibile e lo step non e completato */}
      {!result && periodReady && (
        <Form className="supercutoff-form-wrapper" aria-label="Selected period (locked)">
          <Form.Group widths="equal">
            <Form.Select
              id="revenue-reset-month-select"
              label="Month"
              options={monthOptions}
              value={month}
              disabled
            />
            <Form.Select
              id="revenue-reset-year-select"
              label="Year"
              options={yearOptions}
              value={year}
              disabled
            />
          </Form.Group>
        </Form>
      )}

      {/* Preview — count + tabella dettaglio */}
      {preview && !result && (
        <React.Fragment>
          <div className="supercutoff-preview-box">
            <h4>Preview — budgets to reset</h4>
            <div className="supercutoff-preview-stats">
              <div className="supercutoff-stat">
                <span className="supercutoff-stat-value">{preview.budgets_to_reset}</span>
                <span className="supercutoff-stat-label">Pending budgets</span>
              </div>
            </div>
          </div>

          {preview.budget_details && preview.budget_details.length > 0 && (
            <div className="supercutoff-cr-table-wrapper supercutoff-table-margin-bottom">
              <Table celled compact="very" size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell scope="col">Budget ID</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Project ID</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Status</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Type</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Revenues</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Total Revenues</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {preview.budget_details.map(b => (
                    <Table.Row key={b.budget_id}>
                      <Table.Cell>{b.budget_id}</Table.Cell>
                      <Table.Cell>{b.project_id}</Table.Cell>
                      <Table.Cell>{b.project_status}</Table.Cell>
                      <Table.Cell>{b.status_type}</Table.Cell>
                      <Table.Cell>{formatCurrency(b.revenues)}</Table.Cell>
                      <Table.Cell>{formatCurrency(b.total_revenues)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </React.Fragment>
      )}

      {/* Conferma — role="alert" per annuncio automatico agli screen reader */}
      {preview && !result && confirmVisible && (
        <div
          id="revenue-reset-confirm-desc"
          className="supercutoff-confirm-box"
          role="alert"
        >
          <Icon name="warning sign" color="yellow" size="large" aria-hidden="true" />
          <span className="confirm-text">
            You are about to reset revenues to <strong>€ 0</strong> on{' '}
            <strong>{preview.budgets_to_reset} pending budgets</strong> for{' '}
            <strong>{monthLabel} {year}</strong>.
            This action cannot be undone.
          </span>
        </div>
      )}

      {/* Risultato */}
      {result && (
        <Message positive icon>
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Revenue reset completed</Message.Header>
            <p>
              Revenues reset to zero on <strong>{result.budgets_reset}</strong> budgets.
            </p>
          </Message.Content>
        </Message>
      )}

      <div className="supercutoff-step-actions">
        {!preview && !result && (
          <Button
            primary
            icon
            labelPosition="left"
            onClick={handlePreview}
            loading={loadingPreview}
            disabled={loadingPreview || !periodReady}
          >
            <Icon name="eye" aria-hidden="true" />
            Load preview
          </Button>
        )}

        {preview && !result && !confirmVisible && (
          <Button
            color="orange"
            icon
            labelPosition="left"
            onClick={() => dispatch({ type: 'SHOW_CONFIRM' })}
            disabled={isExecuting || !periodReady}
          >
            <Icon name="play" aria-hidden="true" />
            Execute revenue reset
          </Button>
        )}

        {preview && !result && confirmVisible && (
          <React.Fragment>
            <Button
              color="red"
              icon
              labelPosition="left"
              onClick={handleExecute}
              loading={isExecuting}
              disabled={isExecuting || !periodReady}
              aria-describedby="revenue-reset-confirm-desc"
            >
              <Icon name="check" aria-hidden="true" />
              Confirm reset
            </Button>
            <Button
              basic
              onClick={() => dispatch({ type: 'HIDE_CONFIRM' })}
              disabled={isExecuting}
              aria-describedby="revenue-reset-confirm-desc"
            >
              Cancel
            </Button>
          </React.Fragment>
        )}

        {result && (
          <span className="supercutoff-status-badge completed">
            <Icon name="check" aria-hidden="true" />
            Step completed
          </span>
        )}
      </div>
    </div>
  )
}

StepRevenueReset.propTypes = {
  preview: PropTypes.shape({
    budgets_to_reset: PropTypes.number,
    budget_details: PropTypes.arrayOf(
      PropTypes.shape({
        budget_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        project_status: PropTypes.string,
        status_type: PropTypes.string,
        revenues: PropTypes.number,
        total_revenues: PropTypes.number,
      })
    ),
  }),
  result: PropTypes.shape({ budgets_reset: PropTypes.number }),
  nextPeriod: PropTypes.shape({
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    date_start: PropTypes.string,
    date_end: PropTypes.string,
  }),
  loadingNextPeriod: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  loadingPreview: PropTypes.bool,
  loadingExecute: PropTypes.bool,
}

export default StepRevenueReset
